import { useEffect, useState } from "react";
import { Reveal } from "./Reveal";
import { Clock, MapPin, TrendingUp, Dumbbell, Smartphone, Users, CalendarPlus, ArrowRight, Gift } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { displayTime, googleCalUrl, downloadIcs } from "@/lib/calendar";

const perks = [
  { icon: TrendingUp, label: "Track your progress" },
  { icon: Dumbbell, label: "Strength & conditioning" },
  { icon: Smartphone, label: "Results through the app" },
  { icon: Users, label: "All levels welcome" },
];

export default function Schedule({ onConsult, onSelectPlan }) {
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  const book = (c) => {
    onSelectPlan?.({
      program: "bootcamp",
      note: `Interested in the ${c.name} class (${c.day} ${displayTime(c.start)}) at ${c.location}`,
    });
    onConsult?.();
  };

  return (
    <section id="schedule" className="bg-[#050505] py-24 md:py-40 border-t border-[#222]" data-testid="schedule">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal className="mb-14 md:mb-20">
          <p className="font-mono-accent text-[#005EEA] text-xs mb-4">L.I.F.T. — Lean. Improve. Flex. Train.</p>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
              Class schedule
            </h2>
            <button
              onClick={() => onSelectPlan?.({
                program: "bootcamp",
                note: "FIRST CLASS FREE — I'd like to try a complimentary bootcamp class. Please let me know availability.",
              }) || onConsult?.()}
              className="group inline-flex items-center gap-2 border-2 border-[#005EEA] bg-[#005EEA]/10 text-white px-5 py-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] hover:bg-[#005EEA] transition-colors shadow-[0_0_30px_rgba(0,94,234,0.35)]"
              data-testid="schedule-try-free-btn"
            >
              <Gift size={14} className="text-[#005EEA] group-hover:text-white transition-colors" />
              First Class Free
              <ArrowRight size={12} />
            </button>
          </div>
          <p className="font-mono-accent text-[10px] text-[#666] mt-5">Weekly · Two locations</p>
        </Reveal>

        <div className="border border-[#222]" data-testid="schedule-list">
          {classes.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <div
                className={`group grid grid-cols-[64px_1fr] md:grid-cols-[110px_1fr_150px_auto] items-stretch ${
                  i < classes.length - 1 ? "border-b border-[#222]" : ""
                } hover:bg-[#0b0b0b] transition-colors`}
                data-testid={`class-row-${c.id}`}
              >
                {/* Day badge */}
                <div className="bg-[#005EEA] flex items-center justify-center py-6 md:py-0">
                  <span className="font-display text-white text-xl md:text-2xl leading-none">{c.day}</span>
                </div>

                {/* Class info */}
                <div className="p-5 md:p-7 flex flex-col justify-center">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h3 className="font-display text-white text-xl md:text-2xl leading-none">{c.name}</h3>
                    {c.urgency && (
                      <span
                        className="font-mono-accent text-[9px] uppercase tracking-[0.15em] px-2 py-1 bg-[#005EEA]/15 text-[#005EEA] border border-[#005EEA]/30 leading-none"
                        data-testid={`urgency-${c.id}`}
                      >
                        {c.urgency}
                      </span>
                    )}
                  </div>
                  <p className="flex items-center gap-2 font-mono-accent text-[10px] text-[#005EEA] mb-1.5">
                    <MapPin size={12} /> {c.location}
                  </p>
                  {c.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#666] text-xs hover:text-[#005EEA] transition-colors mb-2 block"
                      data-testid={`class-address-${c.id}`}
                    >
                      {c.address}
                    </a>
                  )}
                  {c.description && <p className="text-[#A0A0A0] text-sm max-w-md">{c.description}</p>}
                  <p className="md:hidden mt-3 flex items-center gap-2 text-white font-display text-lg">
                    <Clock size={15} className="text-[#005EEA]" /> {displayTime(c.start)}
                  </p>
                </div>

                {/* Time (desktop) */}
                <div className="hidden md:flex items-center justify-center gap-2 border-l border-[#222]">
                  <Clock size={16} className="text-[#005EEA]" />
                  <span className="font-display text-white text-2xl leading-none">{displayTime(c.start)}</span>
                </div>

                {/* Actions */}
                <div className="col-span-2 md:col-span-1 flex md:flex-col items-stretch border-t md:border-t-0 md:border-l border-[#222]">
                  <button
                    onClick={() => book(c)}
                    className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-3 md:py-4 font-mono-accent text-[10px] text-white hover:bg-[#005EEA] transition-colors"
                    data-testid={`book-class-${c.id}`}
                  >
                    Book <ArrowRight size={12} />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-3 md:py-4 font-mono-accent text-[10px] text-[#A0A0A0] border-l md:border-l-0 md:border-t border-[#222] hover:text-white hover:bg-[#111] transition-colors"
                        data-testid={`addcal-class-${c.id}`}
                      >
                        <CalendarPlus size={13} /> Add
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#111] border border-[#222] text-white rounded-none">
                      <DropdownMenuItem
                        className="rounded-none focus:bg-[#005EEA] focus:text-white cursor-pointer text-xs"
                        onClick={() => window.open(googleCalUrl(c), "_blank")}
                        data-testid={`gcal-${c.id}`}
                      >
                        Google Calendar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="rounded-none focus:bg-[#005EEA] focus:text-white cursor-pointer text-xs"
                        onClick={() => downloadIcs(c)}
                        data-testid={`ical-${c.id}`}
                      >
                        Apple / Outlook (.ics)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {perks.map((p) => (
              <div key={p.label} className="border border-[#222] bg-[#080808] p-6 flex flex-col items-center text-center gap-3">
                <p.icon className="text-[#005EEA]" size={24} />
                <span className="font-mono-accent text-[10px] text-[#A0A0A0]">{p.label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <p className="mt-10 text-center font-display text-white text-2xl md:text-4xl leading-none">
            Show up. Put in the work. <span className="text-[#005EEA]">Get results.</span>
          </p>
          <p className="mt-4 text-center font-mono-accent text-[10px] text-[#666]">
            Synergy Fitness &nbsp;·&nbsp; Afterburn Performance
          </p>
        </Reveal>
      </div>
    </section>
  );
}
